import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Container,
  CssBaseline,
  Toolbar,
  Typography,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Grid,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import Footer from "../components/Footer";
import Navigation from "../components/Navigation";
import { useApi } from "../hooks/useApi";

export default function AdminAddProduct() {
  const navigate = useNavigate();
  const api = useApi();

  const [formData, setFormData] = React.useState({
    name: "",
    price: "",
  });
  const [creating, setCreating] = React.useState(false);
  const [error, setError] = React.useState(null);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError("Product name is required");
      return;
    }

    const priceNum = parseFloat(formData.price);
    if (isNaN(priceNum) || priceNum < 0) {
      setError("Price must be a valid non-negative number");
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const newProduct = await api.post("/admin/products", {
        name: formData.name.trim(),
        price: priceNum,
      });
      // Navigate to the product detail page
      navigate(`/dashboard/products/${newProduct.id}`);
    } catch (err) {
      console.error("Error creating product:", err);
      setError(err.message || "Failed to create product");
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Toolbar sx={{ minHeight: "48px !important", height: "48px" }} />
          <Box sx={{ px: 3, pt: 1, pb: 1 }}>
            <Breadcrumbs aria-label="breadcrumb">
              <Typography
                color="primary"
                onClick={() => navigate("/dashboard")}
                sx={{
                  textDecoration: "none",
                  cursor: "pointer",
                  fontWeight: 500,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Admin
              </Typography>
              <Typography
                color="primary"
                onClick={() => navigate("/dashboard/products")}
                sx={{
                  textDecoration: "none",
                  cursor: "pointer",
                  fontWeight: 500,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Products
              </Typography>
              <Typography color="text.primary" fontWeight={500}>
                Add Product
              </Typography>
            </Breadcrumbs>
          </Box>
          <Container maxWidth={false} sx={{ mt: 2, mb: 4, flexGrow: 1, px: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/dashboard/products")}
                variant="outlined"
              >
                Back to List
              </Button>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Card>
              <CardContent>
                <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                  Add New Product
                </Typography>

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Product Name"
                        value={formData.name}
                        onChange={handleChange("name")}
                        disabled={creating}
                        required
                        autoFocus
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Price"
                        type="number"
                        value={formData.price}
                        onChange={handleChange("price")}
                        disabled={creating}
                        required
                        inputProps={{
                          step: "0.01",
                          min: "0",
                        }}
                        InputProps={{
                          startAdornment: <Typography sx={{ mr: 1, color: "text.secondary" }}>$</Typography>,
                        }}
                        helperText="Enter the product price"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: "flex", gap: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
                        <Button
                          type="submit"
                          variant="contained"
                          disabled={creating || !formData.name.trim() || !formData.price}
                          startIcon={creating ? <CircularProgress size={16} /> : <SaveIcon />}
                        >
                          {creating ? "Creating..." : "Create Product"}
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => navigate("/dashboard/products")}
                          disabled={creating}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </form>
              </CardContent>
            </Card>
          </Container>
          <Footer />
        </Box>
      </Box>
    </>
  );
}
